import { useEffect, useMemo, useState } from "react";
import { ArtifactCard } from "../agent/ArtifactCard";
import { AssistantMessageToolbar } from "../agent/AssistantMessageToolbar";
import { Markdown } from "../agent/Markdown";
import type { AgentArtifact, AgentToolCall, ChatMessage } from "../../../../shared/types.ts";
import type { SynthesisLearnedBanner } from "../../hooks/useAgent";

export const DEFAULT_SLOW_RESPONSE_DELAY_MS = 8000;

interface AgentUIMessageStreamProps {
  activeTool: AgentToolCall | null;
  activityStatus?: "preparing" | "working" | "retrying" | null;
  brandId?: string | null;
  isStreaming: boolean;
  messages: Array<ChatMessage & { artifacts?: AgentArtifact[] | null }>;
  onPrimaryAction?: (() => void) | null;
  primaryActionLabel?: string | null;
  slowResponseDelayMs?: number;
  streamingText: string;
  synthesisLearned: SynthesisLearnedBanner | null;
  toolProgress: Array<{ id: string; message: string; name: string }>;
}

function deriveProgressLabel(activeTool: AgentToolCall | null, toolProgress: Array<{ id: string; message: string; name: string }>, streamingText: string) {
  const recentProgress = toolProgress.at(-1)?.message ?? "";
  const runtimeHint = `${activeTool?.name ?? ""} ${recentProgress}`.toLowerCase();

  if (activeTool?.status === "failed") return "Tool failed";
  if (activeTool?.status === "success" && !streamingText.trim()) return "Tool completed";
  if (activeTool?.status === "running" && activeTool.name === "terminal") return "Running a command";

  if (runtimeHint.includes("upload") || runtimeHint.includes("image") || runtimeHint.includes("asset")) {
    return "Reviewing attached files";
  }
  if (runtimeHint.includes("search") || runtimeHint.includes("research") || runtimeHint.includes("source") || runtimeHint.includes("context")) {
    return "Reviewing context";
  }
  if (runtimeHint.includes("draft") || streamingText.trim().length > 0) {
    return "Drafting recommendation";
  }
  if (toolProgress.length > 0) {
    return "Reviewing recent findings";
  }
  if (activeTool?.status === "running") return "Using a tool";
  return "Working on your request";
}

function StreamingState({
  activeTool,
  activityStatus,
  isStreaming,
  slowResponseDelayMs = DEFAULT_SLOW_RESPONSE_DELAY_MS,
  streamingText,
  synthesisLearned,
  toolProgress,
}: Pick<AgentUIMessageStreamProps, "activeTool" | "activityStatus" | "isStreaming" | "slowResponseDelayMs" | "streamingText" | "synthesisLearned" | "toolProgress">) {
  const [showSlowResponseFallback, setShowSlowResponseFallback] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const progressLabel = useMemo(
    () => deriveProgressLabel(activeTool, toolProgress, streamingText),
    [activeTool, streamingText, toolProgress],
  );
  const recentProgress = toolProgress.at(-1)?.message ?? "";
  const showRecentProgress = !streamingText && recentProgress.length > 0;
  const progressHistory = showRecentProgress
    ? toolProgress.slice(Math.max(0, toolProgress.length - 3), -1)
    : toolProgress.slice(-2);

  useEffect(() => {
    if (!isStreaming) {
      setShowSlowResponseFallback(false);
      return;
    }
    setShowSlowResponseFallback(false);
    const timer = window.setTimeout(() => {
      setShowSlowResponseFallback(true);
    }, slowResponseDelayMs);
    return () => window.clearTimeout(timer);
  }, [isStreaming, slowResponseDelayMs]);

  useEffect(() => {
    if (!isStreaming) { setElapsedSeconds(0); return; }
    const startedAt = Date.now();
    const timer = window.setInterval(() => setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, [isStreaming]);

  if (!streamingText && !activeTool && toolProgress.length === 0 && !synthesisLearned) {
    return (
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-sm font-semibold text-white">
          HC
        </div>
        <div className="rounded-3xl bg-white px-5 py-4 shadow-sm ring-1 ring-slate-200">
          <p role="status" className="mb-2 text-sm text-slate-600">
            {activityStatus === "retrying" ? "Retrying the agent" : activityStatus === "working" ? "Agent is working" : "Waiting for the agent"} · {elapsedSeconds}s elapsed
          </p>
          <div className="flex gap-1">
            <span className="h-2 w-2 animate-bounce rounded-full bg-slate-300" style={{ animationDelay: "0ms" }} />
            <span className="h-2 w-2 animate-bounce rounded-full bg-slate-300" style={{ animationDelay: "120ms" }} />
            <span className="h-2 w-2 animate-bounce rounded-full bg-slate-300" style={{ animationDelay: "240ms" }} />
          </div>
          {showSlowResponseFallback ? <p className="mt-3 text-sm text-slate-500">This is taking longer than usual. You can keep waiting, or stop and try a narrower ask.</p> : null}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 items-start gap-4">
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-sm font-semibold text-white">
        HC
      </div>
      <div className="min-w-0 max-w-3xl rounded-3xl bg-white px-5 py-4 shadow-sm ring-1 ring-slate-200">
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
          {progressLabel}
        </p>
        {showRecentProgress ? (
          <p className="mb-3 break-words [overflow-wrap:anywhere] text-sm text-slate-500">{recentProgress}</p>
        ) : null}
        {progressHistory.length > 0 ? (
          <div className="mb-3 space-y-1">
            {progressHistory.map((item) => (
              <p key={`${item.id}:${item.message}`} className="break-words [overflow-wrap:anywhere] text-sm text-slate-400">
                {item.message}
              </p>
            ))}
          </div>
        ) : null}
        {streamingText ? (
          <Markdown content={streamingText} className="break-words [overflow-wrap:anywhere] text-[15px] leading-8 text-slate-900" />
        ) : activeTool ? (
          <p className="text-[15px] leading-8 text-slate-600">
            HelloConvo is still working through the next step for you.
          </p>
        ) : null}
        {showSlowResponseFallback ? (
          <p className="mt-3 break-words [overflow-wrap:anywhere] text-sm text-slate-500">
            This is taking longer than usual. You can keep waiting, or stop and try a narrower ask.
          </p>
        ) : null}
        {synthesisLearned ? (
          <p className="mt-3 break-words [overflow-wrap:anywhere] text-sm text-slate-500">{synthesisLearned.summary}</p>
        ) : null}
      </div>
    </div>
  );
}

function EmptyState({
  onPrimaryAction,
  primaryActionLabel,
}: Pick<AgentUIMessageStreamProps, "onPrimaryAction" | "primaryActionLabel">) {
  return (
    <div className="flex flex-1 items-center justify-center px-6">
      {primaryActionLabel && onPrimaryAction ? (
        <button
          type="button"
          onClick={onPrimaryAction}
          className="rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
        >
          {primaryActionLabel}
        </button>
      ) : null}
    </div>
  );
}

function MessageRow({ brandId, message }: { brandId: string | null; message: ChatMessage & { artifacts?: AgentArtifact[] | null } }) {
  const isUser = message.role === "user";
  const isIncomplete = !isUser && (message.metadata?.agent_turn_outcome?.status === "failed" || message.metadata?.agent_turn_outcome?.status === "cancelled" || message.metadata?.agent_turn_outcome?.status === "pending");
  return (
    <div className={`flex min-w-0 items-start gap-4 ${isUser ? "justify-end" : ""}`}>
      {!isUser ? (
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-sm font-semibold text-white">
          HC
        </div>
      ) : null}
      <div className={`min-w-0 max-w-3xl ${isUser ? "text-right" : ""}`}>
        <div className={`mb-2 flex items-center gap-3 text-sm ${isUser ? "justify-end" : ""}`}>
          <span className="font-semibold text-slate-900">{isUser ? "You" : "HelloConvo"}</span>
          {message.created_at ? <span className="font-mono text-xs text-slate-400">{new Date(message.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span> : null}
        </div>
        <div className={`rounded-3xl px-5 py-4 ${isUser ? "bg-brand-50 text-slate-900 ring-1 ring-brand-100" : "bg-white text-slate-900 ring-1 ring-slate-200 shadow-sm"}`}>
          {isIncomplete ? <p role="alert" className="mb-2 text-sm font-semibold text-red-700">Incomplete response · Agent run {message.metadata?.agent_turn_outcome?.status === "pending" ? "interrupted" : message.metadata?.agent_turn_outcome?.status}</p> : null}
          {isUser ? (
            <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-[15px] leading-8">
              {message.content}
            </p>
          ) : (
            message.content
              ? <Markdown content={message.content} className="break-words [overflow-wrap:anywhere] text-[15px] leading-8" />
              : isIncomplete ? <p className="text-sm text-slate-600">No answer was saved. You can try again.</p> : null
          )}
        </div>
        {!isUser && brandId && !isIncomplete ? (
          <>
            {message.artifacts?.map((artifact) => (
              <ArtifactCard
                key={artifact.id}
                name={artifact.name}
                contentType={artifact.content_type}
                preview={artifact.preview}
                artifactKey={artifact.key}
                brandId={brandId}
              />
            ))}
            <AssistantMessageToolbar
              brandId={brandId}
              messageId={message.id}
              content={message.content}
              artifacts={message.artifacts}
            />
          </>
        ) : null}
      </div>
      {isUser ? (
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-sm font-semibold text-brand-700">
          You
        </div>
      ) : null}
    </div>
  );
}

export function AgentUIMessageStream(props: AgentUIMessageStreamProps) {
  const {
    brandId,
    isStreaming,
    messages,
    onPrimaryAction,
    primaryActionLabel,
    slowResponseDelayMs,
  } = props;

  if (messages.length === 0 && !isStreaming) {
    return (
      <EmptyState
        onPrimaryAction={onPrimaryAction}
        primaryActionLabel={primaryActionLabel}
      />
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-1 flex-col gap-6 px-6 py-8">
      {messages.map((message) => (
        <MessageRow key={message.id} brandId={brandId ?? null} message={message} />
      ))}
      {isStreaming ? <StreamingState {...props} slowResponseDelayMs={slowResponseDelayMs} /> : null}
    </div>
  );
}
